<template>
  <div class="game-list-wrapper" :class="className">
    <template v-if="!loading">
      <van-grid class="game-list" :gutter="10" :column-num="cols" v-if="gameItems && gameItems.length > 0">
        <van-grid-item v-for="(item, index) in gameItems" :key="index">
          <slot v-bind="item">
            <game-card :item="item" :index="index" />
          </slot>
        </van-grid-item>
      </van-grid>
      <van-empty image="/images/empty.png" image-size="150" description="无结果" v-else />
      <div class="loading-more flex-center" ref="loadingMore" v-if="isLoadAll && !loading">
        <van-loading color="var(--theme-primary-color)" />
      </div>
    </template>
    <template v-else>
      <div class="loading-box flex-center">
        <custom-loading />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, defineExpose, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs, useAppStore, useGameStore, useUserStore } from '@star/stores';
import {
  GameBigTypesEnum,
  GameChildTypesEnum,
  GameMainKeysEnum,
  GameMainTypesEnum,
  gameMainTypesMap,
} from '@star/constants';
import { getGameImageSrc, transformGameData } from '@star/plugins';
import { dealBYList, dealCollectData, dealDZList, dealQPList, getHotGamesData, getTryGamesData } from '@/plugins/game';
import { G_goLogin } from '@/plugins/public_utils';
import { concat, filter, includes, keys, map, slice, split, startsWith, toNumber, values } from 'lodash-es';
import GameCard from './GameCard.vue';
import type { RefreshHomeGames } from '@star/utils';
import { emitter, EventEnum } from '@star/utils';
import CustomLoading from '@/components/CustomLoading.vue';
import { useIntersectionObserver } from '@vueuse/core';

type Props = {
  cols?: number;
  type?: GameMainTypesEnum;
};

const props = withDefaults(defineProps<Props>(), {
  cols: 2,
});

const appStore = useAppStore();
const gameStore = useGameStore();
const {
  activeHomeMainMenuTab,
  activeHomeSubMenuTab,
  activeHomeChildMenuTab,
  smallClassGames,
  hallData,
  gameBigClass,
  newHotGames,
  myCollect,
  lotteryThird,
  playGames,
  chessPlayGames,
  fishingPlayGames,
  qklPlayGames,
  gamesTry,
  gamesTryList,
  gamesTryActive,
} = storeToRefs(gameStore);
const { isLogin, userInfo } = storeToRefs(useUserStore());
const { isPageLoading } = storeToRefs(appStore);

const gamesData = ref<Record<string, any>>();
const activeGameList = ref<any[]>([]);
const gameItems = ref<any[]>([]);
const loading = ref<boolean>(true);
const loadingMore = ref(null);
const searchValue = ref<string>();

useIntersectionObserver(loadingMore, ([{ isIntersecting }], observerElement) => {
  if (isIntersecting) {
    getPageGameItems();
  }
});

const className = computed(() => {
  return gameMainTypesMap[toNumber(activeHomeMainMenuTab.value)];
});

const isLoadAll = computed(() => {
  return (searchGameList.value?.length ?? 0) > (gameItems.value?.length ?? 0);
});

const searchGameList = computed(() => {
  return searchValue.value
    ? filter(activeGameList.value, item => includes(item.play_cname, searchValue.value))
    : activeGameList.value;
});

// reset：静态刷新时为false
const getGamesData = async (options?: RefreshHomeGames) => {
  const { mainType, reset = true } = options || {};
  const type = toNumber(props.type || activeHomeMainMenuTab.value);
  console.log(type, reset, 'getGamesData type --------------', mainType);
  if (reset) {
    resetHandle();
  }
  try {
    loading.value = reset;
    switch (type) {
      case GameMainTypesEnum.COLLECT:
        const data = await getCollectData();
        gamesData.value = { [type]: data };
        if (mainType === GameMainTypesEnum.ELECTRONIC) {
          await gameStore.reqPlayGames({ isDz: true });
        } else if (mainType === GameMainTypesEnum.BLOCK_THIRD) {
          await gameStore.reqPlayGames({ isQkl: true });
        } else if (mainType === GameMainTypesEnum.FISHING) {
          await gameStore.reqPlayGames({ isBy: true });
        } else if (mainType === GameMainTypesEnum.CHESS) {
          await gameStore.reqPlayGames();
        }
        break;
      case GameMainTypesEnum.RECENT:
        const recentData = await getRecentData(type);
        gamesData.value = { [type]: recentData };
        break;
      case GameMainTypesEnum.ELECTRONIC:
        const elec_data =
          reset && keys(playGames.value).length ? playGames.value : await gameStore.reqPlayGames({ isDz: true });
        gamesData.value = {
          ...elec_data,
          [GameMainTypesEnum.HOT_ELECTRONIC]: smallClassGames.value?.[GameMainKeysEnum.HOT_ELECTRONIC],
        };
        console.log(gamesData.value, smallClassGames.value, 'gamesData.value ELECTRONIC');
        break;
      case GameMainTypesEnum.LOTTERY:
        // console.log(gamesData.value, 'gamesData.value LOTTERY');
        break;
      case GameMainTypesEnum.FISHING:
        const fishing_data =
          reset && keys(fishingPlayGames.value).length
            ? fishingPlayGames.value
            : await gameStore.reqPlayGames({ isBy: true });
        gamesData.value = {
          ...fishing_data,
          [GameMainTypesEnum.HOT_FISHING]: filter(
            smallClassGames.value?.[GameMainKeysEnum.HOT_FISHING],
            item => item.upper_play_id
          ),
        };
        console.log(gamesData.value, smallClassGames.value, 'gamesData.value FISHING');
        break;
      case GameMainTypesEnum.LIVE:
        break;
      case GameMainTypesEnum.CHESS:
        const chessData =
          reset && keys(chessPlayGames.value).length
            ? chessPlayGames.value
            : await gameStore.reqPlayGames({ isQp: true });
        gamesData.value = {
          ...chessData,
          [GameMainTypesEnum.HOT_CHESS]: smallClassGames.value?.[GameMainKeysEnum.HOT_CHESS],
        };
        console.log(gamesData.value, smallClassGames.value, 'getGamesData CHESS');
        break;
      case GameMainTypesEnum.BLOCK_THIRD:
        const qkl_data =
          reset && keys(qklPlayGames.value).length ? qklPlayGames.value : await gameStore.reqPlayGames({ isQkl: true });
        gamesData.value = {
          ...qkl_data,
          [GameMainTypesEnum.HOT_BLOCK_THIRD]: smallClassGames.value?.[GameMainKeysEnum.HOT_BLOCK_THIRD],
        };
        console.log(gamesData.value, smallClassGames.value, 'gamesData.value BLOCK_THIRD');
        break;
      default:
        gamesData.value = undefined;
        break;
    }
    console.log(gamesData.value, `------------ gamesData.value ${type}`);
    await getActiveGameList();
  } finally {
    loading.value = false;
    appStore.setPullRefresh(false);
  }
};

// 电子，捕鱼，棋牌，区块链最前面的热门不用排序
const isAllNotSortFn = (type, _subType) => {
  const isTypeMatch = [
    GameMainTypesEnum.ELECTRONIC,
    GameMainTypesEnum.FISHING,
    GameMainTypesEnum.CHESS,
    GameMainTypesEnum.BLOCK_THIRD,
  ].includes(type);
  const allHotKey = `${GameMainTypesEnum.HOT_GAME + toNumber(activeHomeMainMenuTab.value)}`;
  const isSubTypeMatch = allHotKey == _subType;
  return isTypeMatch && isSubTypeMatch;
};

const getActiveGameList = async (subType?: GameMainTypesEnum, childType?: GameChildTypesEnum) => {
  const type = toNumber(props.type || activeHomeMainMenuTab.value);
  let _subType = subType ?? activeHomeSubMenuTab.value;
  _subType = _subType && !isNaN(_subType) ? toNumber(_subType) : _subType;
  const _childType = childType ?? activeHomeChildMenuTab.value;
  console.log([type, _subType, childType], '[type, _subType, childType] ++++++++++++++');
  console.log(gameMainTypesMap[_subType], smallClassGames.value, 'smallClassGames.value ++++++++++++++');
  const isAllNotSort = isAllNotSortFn(type, _subType);
  switch (type) {
    case GameMainTypesEnum.HOT_GAME:
      activeGameList.value = map(getHotGamesData(newHotGames.value), (item, index) => ({
        ...item,
        gameIndex: index,
        mainType: GameMainTypesEnum.HOT_GAME,
      }));
      break;
    case GameMainTypesEnum.TRIAL:
      gameItems.value = [];
      activeGameList.value = map(
        getTryGamesData(gamesTry.value[gamesTryActive.value]?.subList || []),
        (item, index) => ({
          ...item,
          gameIndex: index,
          mainType: GameMainTypesEnum.TRIAL,
        })
      );
      console.log(activeGameList.value, '--------------TRIAL');
      break;
    case GameMainTypesEnum.COLLECT:
      gameItems.value = [];
      activeGameList.value = map(gamesData.value?.[type], (item, index) => {
        let imgUrl = '';
        if ([GameBigTypesEnum.SPORTS, GameBigTypesEnum.LIVE, GameBigTypesEnum.LOTTERY].includes(item.big_type)) {
          imgUrl = item.gameImgUrl;
        } else {
          const _msg = item.msg ? JSON.parse(item.msg) : {};
          imgUrl = _msg?.gameImgUrl || item.thumbnail_img_url;
        }
        return {
          ...item,
          gameImgUrl: imgUrl,
        };
      });
      console.log(activeGameList.value, '--------------COLLECT');
      break;
    case GameMainTypesEnum.RECENT:
      activeGameList.value = gamesData.value?.[type];
      console.log('--------------RECENT');
      break;
    case GameMainTypesEnum.ELECTRONIC:
      const electronicData = transformGameData(gamesData.value?.[_subType], type);
      console.log(electronicData, gamesData.value, 'activeListData ELECTRONIC');
      activeGameList.value = await getChildTypeData(electronicData, _childType, isAllNotSort);
      break;
    case GameMainTypesEnum.BLOCK_THIRD:
      const blockData = transformGameData(gamesData.value?.[_subType], type);
      console.log(blockData, gamesData.value, 'activeListData BLOCK');
      activeGameList.value = await getChildTypeData(blockData, _childType, isAllNotSort);
      break;
    case GameMainTypesEnum.LOTTERY:
      const _lotteryData = map(
        filter(gameBigClass.value?.[GameMainKeysEnum.LOTTERY], (item: any) => item.state !== 3),
        item => {
          return {
            ...item,
            gameImgUrl: getGameImageSrc(item, { type: GameMainKeysEnum.LOTTERY, imgType: 2 }),
          };
        }
      );
      activeGameList.value = await getChildTypeData(_lotteryData, _childType);
      break;
    case GameMainTypesEnum.FISHING:
      const fishingData = dealBYList(gamesData.value?.[_subType]);
      activeGameList.value = await getChildTypeData(fishingData, _childType, isAllNotSort);
      console.log('fishingData22', fishingData, activeGameList.value);
      break;
    case GameMainTypesEnum.LIVE:
      const liveData = map(
        filter(gameBigClass.value?.[GameMainKeysEnum.LIVE], (item: any) => item.state !== 3),
        item => {
          return {
            ...item,
            gameImgUrl: getGameImageSrc(item, { type: GameMainKeysEnum.LIVE, imgType: 2 }),
          };
        }
      );
      console.log(liveData, gameBigClass.value, 'activeListData LIVE');
      activeGameList.value = await getChildTypeData(liveData, _childType);
      break;
    case GameMainTypesEnum.SPORTS:
      const sportsData = map(
        filter(gameBigClass.value?.[GameMainKeysEnum.SPORTS], (item: any) => item.state !== 3),
        item => {
          return {
            ...item,
            gameImgUrl: getGameImageSrc(item, { type: GameMainKeysEnum.SPORTS, imgType: 2 }),
          };
        }
      );
      activeGameList.value = await getChildTypeData(sportsData, _childType);
      // console.log('--------------SPORTS');
      break;
    case GameMainTypesEnum.CHESS:
      const chessData = dealQPList(gamesData.value?.[_subType]) as any[];
      console.log(chessData, '--------------activeListData CHESS');
      activeGameList.value = await getChildTypeData(chessData, _childType, isAllNotSort);
      break;
    default:
      activeGameList.value = [];
      // console.log('--------------default');
      break;
  }
  getPageGameItems();
};

const getChildTypeData = async (data: any[], type: GameChildTypesEnum, isAllNotSort?: boolean): Promise<any> => {
  if (type === GameChildTypesEnum.ALL) {
    const result = isAllNotSort ? data : data.sort((a, b) => a.play_sort - b.play_sort);
    return Promise.resolve(result);
  } else if (type === GameChildTypesEnum.HOT) {
    const result = filter(data, item => item?.play_is_hot === 1).sort((a, b) => a.hot_play_sort - b.hot_play_sort);
    return Promise.resolve(result);
  } else if (type === GameChildTypesEnum.RECENT) {
    const mainType = toNumber(props.type || activeHomeMainMenuTab.value);
    const result = await getRecentData(mainType, data);
    return Promise.resolve(result);
    // return filter(data, item => (item.is_top ?? item.data?.is_top) > 0)
  } else if (type === GameChildTypesEnum.COLLECT) {
    return Promise.resolve(filter(data, item => (item.play_collection ?? item.data?.play_collection) === 2));
  } else {
    return Promise.resolve([]);
  }
};

const getHotChessData = (data: Record<string, any>) => {
  return filter(concat(...values(data)), item => item.hot_val > 0);
};

// 收藏的游戏
const getCollectData = async () => {
  if (!isLogin.value) {
    G_goLogin();
    return Promise.resolve([]);
  }
  const res = await gameStore.getPlayCollection();
  return Promise.resolve(Object.freeze(dealCollectData(res || [])));
};

// 最近玩过
const getRecentData = async (mainType: GameMainTypesEnum, data?: Record<string, any>[]) => {
  if (!isLogin.value) {
    await G_goLogin();
    return Promise.resolve([]);
  }
  const localKey = `RECENT_GAME_${userInfo.value.cust_id || ''}`;
  const localData = localStorage.getItem(localKey);
  let recentGameIds = localData ? split(localData, ',') : [];
  if (recentGameIds.length > 0) {
    if (data) {
      console.log(recentGameIds, activeHomeSubMenuTab.value, mainType, 'getRecentData recentGameIds');
      console.log(data, 'getRecentData data');
      const subType = toNumber(activeHomeSubMenuTab.value);
      let ids: string[] = [];
      let games: any[] = [];
      let key: string = '';
      switch (mainType) {
        case GameMainTypesEnum.LOTTERY:
          ids = map(
            filter(recentGameIds, val => startsWith(val, 'CP')),
            val => split(val, '-')?.[1]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, `${item.play_id}`);
          });
          break;
        case GameMainTypesEnum.ELECTRONIC:
          key = subType === GameMainTypesEnum.HOT_ELECTRONIC ? 'DZ' : `DZ-${activeHomeSubMenuTab.value}`;
          ids = map(
            filter(recentGameIds, val => startsWith(val, key)),
            val => split(val, '-')?.[2]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, item.play_name);
          });
          break;
        case GameMainTypesEnum.BLOCK_THIRD:
          key = subType === GameMainTypesEnum.HOT_BLOCK_THIRD ? 'QKL' : `QKL-${activeHomeSubMenuTab.value}`;
          ids = map(
            filter(recentGameIds, val => startsWith(val, key)),
            val => split(val, '-')?.[2]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, item.play_name);
          });
          break;
        case GameMainTypesEnum.FISHING:
          key = subType === GameMainTypesEnum.HOT_FISHING ? 'BY' : `BY-${activeHomeSubMenuTab.value}`;
          ids = map(
            filter(recentGameIds, val => startsWith(val, key)),
            val => split(val, '-')?.[2]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, `${item.play_id}`);
          });
          break;
        case GameMainTypesEnum.LIVE:
          ids = map(
            filter(recentGameIds, val => startsWith(val, 'ZR')),
            val => split(val, '-')?.[1]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, `${item.play_id}`);
          });
          break;
        case GameMainTypesEnum.SPORTS:
          ids = map(
            filter(recentGameIds, val => startsWith(val, 'TY')),
            val => split(val, '-')?.[1]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, `${item.play_id}`);
          });
          break;
        case GameMainTypesEnum.CHESS:
          key = subType === GameMainTypesEnum.HOT_CHESS ? 'QP' : `QP-${activeHomeSubMenuTab.value}`;
          ids = map(
            filter(recentGameIds, val => startsWith(val, key)),
            val => split(val, '-')?.[2]
          ).reverse();
          games = filter(data, item => {
            return includes(ids, item.play_name);
          });
          break;
      }
      console.log(games, 'getRecentData games');
      return games;
    } else {
      const gamesArr: any[] = [];
      // const hotFishing = gameStore.gameBigClass?.[GameMainKeysEnum.FISHING]?.filter(item => item.state !== 3) || [];
      const liveGames = gameStore.gameBigClass?.[GameMainKeysEnum.LIVE]?.filter(item => item.state !== 3) || [];
      const sportGames = gameStore.gameBigClass?.[GameMainKeysEnum.SPORTS]?.filter(item => item.state !== 3) || [];
      const lotteryGames = gameStore.gameBigClass?.[GameMainKeysEnum.LOTTERY]?.filter(item => item.state !== 3) || [];
      const data1 = !keys(playGames.value).length ? await gameStore.reqPlayGames({ isDz: true }) : playGames.value;
      const data2 = !keys(chessPlayGames.value).length
        ? await gameStore.reqPlayGames({ isQp: true })
        : chessPlayGames.value;
      const data3 = !keys(fishingPlayGames.value).length
        ? await gameStore.reqPlayGames({ isBy: true })
        : fishingPlayGames.value;
      const data4 = !keys(qklPlayGames.value).length
        ? await gameStore.reqPlayGames({ isQkl: true })
        : qklPlayGames.value;
      recentGameIds.forEach(v => {
        const gameInfo = v.split('-');
        const gtype = gameInfo[0];
        const sid = gameInfo[1];
        const gid = gameInfo[2];
        let _game: any;
        if (gtype === 'ZR') {
          _game = liveGames?.find(v => v.play_id == sid);
          if (_game) {
            _game.gameImgUrl = `/images/game/live/g_${_game.play_id}.png`;
          }
        } else if (gtype === 'TY') {
          _game = sportGames?.find(v => v.play_id == sid);
          if (_game) {
            _game.gameImgUrl = `/images/game/sports/g_${_game.play_id}.png`;
          }
        } else if (gtype === 'CP') {
          // console.log(lotteryGames, 'lotteryGames ----------------');
          _game = lotteryGames?.find(v => v.play_id == sid);
          if (_game) {
            _game.gameImgUrl = `/images/game/lottery/g_${_game.play_id}.png`;
          }
        } else if (gtype === 'QKL') {
          _game = data4?.[sid]?.find(v => v.play_name == gid);
        } else if (gtype === 'BY') {
          _game = data3?.[sid]?.find(v => v.play_id == gid);
        } else if (gtype === 'DZ') {
          _game = data1?.[sid]?.find(v => v.play_name == gid);
        } else {
          _game = data2?.[sid]?.find(v => v.play_name == gid);
        }
        if (_game) {
          _game.gameType = gtype;
          if (gtype === 'QP') {
            gamesArr.push(...dealQPList([_game]));
          } else if (gtype === 'DZ' || gtype === 'QKL') {
            gamesArr.push(...dealDZList([_game]));
          } else if (gtype === 'BY') {
            gamesArr.push(...dealBYList([_game]));
          } else {
            gamesArr.push(_game);
          }
        }
      });
      return Promise.resolve(gamesArr);
    }
  }
  return Promise.resolve([]);
};

const resetHandle = () => {
  activeGameList.value = [];
  gameItems.value = [];
};

const getPageGameItems = () => {
  const lastIndex = gameItems.value?.length ?? 0;
  gameItems.value = [...gameItems.value, ...slice(searchGameList.value, lastIndex, lastIndex + 8)];
  // console.log(lastIndex, 'lastIndex 66666666666666666');
  // console.log(gameItems.value, 'gameItems.value 88888888888888888888888');
};

// 彩票数据
const getLotteryData = async () => {
  // await gameStore.purchaseHall({
  //   play_cat: 1,
  //   size: 16,
  //   type: 0,
  // });
  // await gameStore.reqTianChengCp();
};

watch(
  [() => activeHomeMainMenuTab.value, () => smallClassGames.value],
  async ([value, value2], [oldValue, oldValue2]) => {
    console.log(value, oldValue, 'watch activeHomeMainMenuTab --------------');
    if ((value && value !== oldValue) || Object.keys(oldValue).length !== Object.keys(oldValue2).length) {
      await getGamesData();
    }
  }
);

// 试玩列表刷新
watch(
  () => gamesTryActive.value,
  async (value, oldValue) => {
    console.log(value, oldValue, 'watch gamesTryActive --------------');
    await getActiveGameList();
  }
);

watch(
  () => [activeHomeSubMenuTab.value, activeHomeChildMenuTab.value],
  async (value, oldValue) => {
    console.log(value, oldValue, 'watch activeHomeSubMenuTab --------------');
    if (value[0] !== oldValue[0] || value[1] !== oldValue[1]) {
      resetHandle();
      await getActiveGameList(value[0] && !isNaN(value[0]) ? toNumber(value[0]) : value[0], value[1]);
    }
  }
);

const searchGames = (val?: string) => {
  console.log(val, activeGameList.value);
  if (val || searchValue.value) {
    searchValue.value = val;
    gameItems.value = [];
  }
};

defineExpose({
  getGamesData,
});

emitter.on(EventEnum.REFRESH_HOME_GAMES, getGamesData);
emitter.on(EventEnum.HOME_SEARCH_GAME, searchGames);
emitter.on(EventEnum.TRY_SEARCH_GAME, searchGames);

onMounted(async () => {
  setTimeout(async () => {
    // console.log(isPageLoading.value, [activeHomeMainMenuTab.value, activeHomeSubMenuTab.value, activeHomeChildMenuTab.value], 'onMounted isPageLoading --------------');
    // console.log(isPageLoading.value, '00000000000000000 onMounted gameList 00000000000000');
    if (!isPageLoading.value) {
      await getGamesData();
    }
  }, 0);
});

onUnmounted(async () => {
  emitter.off(EventEnum.REFRESH_HOME_GAMES);
  emitter.off(EventEnum.HOME_SEARCH_GAME);
  emitter.off(EventEnum.TRY_SEARCH_GAME);
});
</script>

<style lang="less" scoped>
.game-list-wrapper {
  margin-top: 36px;
  margin-bottom: 240px;

  :deep(.van-grid) {
    .van-grid-item__content {
      width: 100%;
      padding: 0;
      background: transparent;
    }

    .van-image__error,
    .van-image__loading {
      background: transparent;
    }
  }

  .loading-more {
    margin-top: var(--footer-height);
    margin-bottom: 60px;
  }

  .loading-box {
    min-height: 40vh;
  }

  .van-empty {
    padding: 90px 0 300px;
  }
}
</style>
